import { useContextEnsuringProvider } from '@iheartjane/utils';
import type { ReactNode } from 'react';
import { createContext, useCallback, useMemo, useRef } from 'react';

type FieldName = string;
type FieldElement = HTMLInputElement | HTMLTextAreaElement;

interface SeoFormContextType {
  insertVariable: (fieldName: FieldName, variable: string) => void;
}

const SeoFormContext = createContext<SeoFormContextType | null>(null);
SeoFormContext.displayName = 'SeoForm';

export const useSeoForm = () => useContextEnsuringProvider(SeoFormContext);

/**
 * Writes through the prototype setter so React's value tracker registers the change and the
 * field's own onInput/onChange fires, updating reefer's internal state and react-hook-form together.
 */
const setNativeValue = (element: FieldElement, value: string) => {
  const prototype =
    element instanceof HTMLTextAreaElement
      ? HTMLTextAreaElement.prototype
      : HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(prototype, 'value')?.set?.call(
    element,
    value
  );
  element.dispatchEvent(new Event('input', { bubbles: true }));
};

export const SeoFormProvider = ({ children }: { children: ReactNode }) => {
  const formRef = useRef<HTMLDivElement>(null);

  const insertVariable = useCallback(
    (fieldName: FieldName, variable: string) => {
      const element = formRef.current?.querySelector<FieldElement>(
        `input[name="${fieldName}"], textarea[name="${fieldName}"]`
      );
      if (!element) return;

      const currentValue = element.value;
      const position =
        document.activeElement === element
          ? (element.selectionStart ?? currentValue.length)
          : currentValue.length;
      const variableText = `{{ ${variable} }}`;
      const newCursorPosition = position + variableText.length;

      setNativeValue(
        element,
        currentValue.slice(0, position) +
          variableText +
          currentValue.slice(position)
      );
      element.focus({ preventScroll: true });
      element.setSelectionRange(newCursorPosition, newCursorPosition);
    },
    []
  );

  const contextValue = useMemo(() => ({ insertVariable }), [insertVariable]);

  return (
    <SeoFormContext.Provider value={contextValue}>
      <div ref={formRef}>{children}</div>
    </SeoFormContext.Provider>
  );
};
